/**
 * LeadActionToolbar — compact mock-aligned actions (Convert / Edit / Provider / More).
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  CheckCircle,
  ContentCopy,
  Download,
  Edit,
  LocalShipping,
  Message,
  MoreHoriz,
  PictureAsPdf,
  Save,
  Schedule,
  Transform,
  WhatsApp,
} from "@mui/icons-material";
import FollowUpMention from "../../followups/components/FollowUpMention.jsx";

const LeadActionToolbar = ({
  lead,
  isEditing,
  isLeadConverted,
  getConvertedQuoteId,
  getOpportunityId,
  convertingToOpportunity,
  isDownloadingZip,
  isDownloadingPdf,
  onAccept,
  onReject,
  onConvert,
  onConvertOpportunity,
  onViewOpportunity,
  onViewQuote,
  onEdit,
  onSave,
  onCancelEdit,
  onTemplate,
  onDownload,
  onDownloadPdf,
  onWhatsApp,
  onPublicCopy,
  onFollowUp,
  onFollowUpNavigate,
  onAssignProvider,
  updateLoading,
}) => {
  const [moreAnchor, setMoreAnchor] = useState(null);
  const moreOpen = Boolean(moreAnchor);

  if (isEditing) {
    return (
      <Box display="flex" gap={1} flexWrap="wrap">
        <Button
          startIcon={<Save />}
          onClick={onSave}
          disabled={updateLoading}
          variant="contained"
          color="primary"
          size="medium"
        >
          Save
        </Button>
        <Button
          startIcon={<CancelIcon />}
          onClick={onCancelEdit}
          variant="outlined"
          size="medium"
        >
          Cancel
        </Button>
      </Box>
    );
  }

  const opportunityId = getOpportunityId?.(lead);
  const converted = isLeadConverted?.(lead);
  const canConvert =
    (lead.status === "new" || lead.status === "accepted") && !converted;

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={1}
      alignItems="center"
      data-testid="lead-action-toolbar"
    >
      {canConvert && (
        <Button
          startIcon={<Transform />}
          onClick={onConvert}
          color="success"
          variant="contained"
          size="medium"
          sx={{ fontWeight: 600 }}
        >
          Convert to Quote
        </Button>
      )}
      {converted && (
        <Button
          variant="outlined"
          color="info"
          size="medium"
          onClick={() => onViewQuote(getConvertedQuoteId(lead))}
        >
          View Quote
        </Button>
      )}
      <Button
        startIcon={<Edit />}
        onClick={onEdit}
        variant="outlined"
        size="medium"
      >
        Edit Lead
      </Button>
      <Button
        startIcon={<LocalShipping />}
        onClick={onAssignProvider}
        variant="outlined"
        size="medium"
      >
        Assign Provider
      </Button>

      {lead.status === "new" && (
        <>
          <Button
            startIcon={<CheckCircle />}
            onClick={onAccept}
            color="success"
            variant="outlined"
            size="medium"
          >
            Accept
          </Button>
          <Button
            startIcon={<CancelIcon />}
            onClick={onReject}
            color="error"
            variant="outlined"
            size="medium"
          >
            Reject
          </Button>
        </>
      )}

      <FollowUpMention
        summary={lead?.followUpSummary}
        onClick={onFollowUpNavigate}
        size="medium"
        variant="filled"
      />

      <IconButton
        aria-label="More actions"
        onClick={(e) => setMoreAnchor(e.currentTarget)}
        size="medium"
      >
        <MoreHoriz />
      </IconButton>

      <Menu
        anchorEl={moreAnchor}
        open={moreOpen}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {lead.status === "new" && !converted && (
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              onConvertOpportunity?.();
            }}
            disabled={convertingToOpportunity}
          >
            <ListItemIcon>
              <Transform fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {convertingToOpportunity
                ? "Creating opportunity..."
                : "Convert to Opportunity"}
            </ListItemText>
          </MenuItem>
        )}
        {opportunityId && (
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              onViewOpportunity(opportunityId);
            }}
          >
            <ListItemIcon>
              <Transform fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Opportunity</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            onTemplate?.();
          }}
        >
          <ListItemIcon>
            <Message fontSize="small" />
          </ListItemIcon>
          <ListItemText>Generate Message</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            onWhatsApp?.();
          }}
        >
          <ListItemIcon>
            <WhatsApp fontSize="small" />
          </ListItemIcon>
          <ListItemText>WhatsApp</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            onPublicCopy?.();
          }}
        >
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy public summary</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            onFollowUp?.();
          }}
        >
          <ListItemIcon>
            <Schedule fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Follow-up</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            onDownload?.();
          }}
          disabled={isDownloadingZip}
        >
          <ListItemIcon>
            {isDownloadingZip ? (
              <CircularProgress size={18} />
            ) : (
              <Download fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>Download ZIP</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            onDownloadPdf?.();
          }}
          disabled={isDownloadingPdf}
        >
          <ListItemIcon>
            {isDownloadingPdf ? (
              <CircularProgress size={18} />
            ) : (
              <PictureAsPdf fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default LeadActionToolbar;
